// Story 1.32 — TanStack Query hook:
// GET /api/v1/admin/financial-tools/users/:user_id/refundable-topups.
//
// Powers the manual-refund tool's source-top-up picker. `enabled: !!userId`
// — we don't fire until the admin enters a valid UUID in the user-lookup
// box.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type RefundableTopUpsFilters = {
  page: number;
  page_size: number;
};

export const DEFAULT_REFUNDABLE_TOPUPS_FILTERS: RefundableTopUpsFilters = {
  page: 1,
  page_size: 50,
};

export type RefundableTopUpItem = {
  ledger_entry_id: string;
  baas_provider_ref: string;
  original_amount_cents: number;
  already_refunded_cents: number;
  // Story 6.14 AC16 — renamed from remaining_refundable_cents. Naive
  // original − already_refunded; NOT the user-obtainable refundable figure.
  remaining_after_refunds_cents: number;
  created_at: string;
  days_since_topup: number;
  card_last4: string | null;
  baas_payment_intent_id: string | null;
};

export type RefundableTopUpsResponse = {
  data: RefundableTopUpItem[];
  page: number;
  page_size: number;
  has_next: boolean;
};

function buildQueryString(filters: RefundableTopUpsFilters): string {
  const params = new URLSearchParams();
  params.set("page", String(filters.page));
  params.set("page_size", String(filters.page_size));
  return params.toString();
}

async function fetchRefundableTopUps(
  userId: string,
  filters: RefundableTopUpsFilters,
): Promise<RefundableTopUpsResponse> {
  const qs = buildQueryString(filters);
  const response = await fetch(
    `/api/v1/admin/financial-tools/users/${userId}/refundable-topups?${qs}`,
    { credentials: "include" },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Refundable top-ups fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as RefundableTopUpsResponse;
}

export function useAdminRefundableTopUps(
  userId: string,
  filters: RefundableTopUpsFilters,
) {
  return useQuery<RefundableTopUpsResponse, Error>({
    queryKey: ["admin-financial-tools-refundable-topups", userId, filters],
    queryFn: () => fetchRefundableTopUps(userId, filters),
    enabled: !!userId,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}
